import type { ReactNode } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { createContext } from 'use-context-selector';
import { useApolloClient } from '@apollo/client';
import { useDndMonitor } from '@dnd-kit/core';
import { useSelectedAssets } from '@frameio/selection';
import { useIsCSCProject } from '../../../shared';
import { useHandleDndOperation } from '../hooks/useHandleDndOperation';
import type {
  AssetDndActive,
  AssetDndInfo,
  AssetDndOver,
} from '../utils/types';

export type VersionStackLifecycle = 'over' | 'dropped' | 'completed' | null;

export type DndOpsContextValue = {
  active: AssetDndInfo | null;
  over: AssetDndInfo | null;
  draggingIds: Set<string>;
  versionStackLifecycle: {
    phase: VersionStackLifecycle;
    toId: string;
    fromId: string;
  } | null;
  onOperationComplete: () => void;
};

const DEFAULT_STATE: DndOpsContextValue = {
  active: null,
  over: null,
  draggingIds: new Set(),
  versionStackLifecycle: null,
  onOperationComplete: noop,
};

export const DndOpsContext = createContext<DndOpsContextValue>(DEFAULT_STATE);

export const DndOpsContextProvider = memo(
  ({ children }: { children: ReactNode }) => {
    const [state, setState] = useState<DndOpsContextValue>(DEFAULT_STATE);
    const selectionIds = useSelectedAssets();
    const isCSCProject = useIsCSCProject();
    const onOperationComplete = useCallback(() => {
      setState(DEFAULT_STATE);
    }, []);
    const handleDndOperation = useHandleDndOperation(
      setState,
      onOperationComplete
    );
    const { cache } = useApolloClient();

    useDndMonitor({
      onDragCancel: () => {
        setState(DEFAULT_STATE);
      },
      onDragEnd: () => {
        handleDndOperation({
          activeInfo: state.active,
          cache,
          draggingIds: state.draggingIds,
          overInfo: state.over,
          isCSCProject,
        });
      },
      onDragMove: noop,
      onDragOver: ({ over }: { over: AssetDndOver }) => {
        if (!over) {
          setState((prev) => ({ ...prev, over: null }));
          return;
        }
        setState((prev) => ({
          ...prev,
          over: {
            id: removeZoneFromId(over.id as string),
            zone: over.data?.current?.zone ?? null,
          },
        }));
      },
      onDragStart: ({ active }: { active: AssetDndActive }) => {
        const id = removeZoneFromId(active?.id as string);
        if (!active) return;
        setState((prev) => ({
          ...prev,
          active: {
            id,
            zone: active.data?.current?.zone ?? null,
          },
          draggingIds: selectionIds.has(id) ? selectionIds : new Set([id]),
        }));
      },
    });

    return (
      <DndOpsContext.Provider
        value={useMemo(
          () => ({ ...state, onOperationComplete }),
          [onOperationComplete, state]
        )}
      >
        {children}
      </DndOpsContext.Provider>
    );
  }
);

const ID_DELIM = ':';

export const removeZoneFromId = (id: string) => id.split(ID_DELIM)[0];
export const addZoneToId = (id: string, zone: string | null) =>
  zone ? `${id}${ID_DELIM}${zone}` : id;
